import React from 'react';
import * as mui from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DnsIcon from '@mui/icons-material/Dns';
import StorageIcon from '@mui/icons-material/Storage';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HistoryIcon from '@mui/icons-material/History';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SecurityIcon from '@mui/icons-material/Security';

import { DashboardCard, SectionHeader, StatsOverview, formatDate } from './UIComponents';
import TerminatedUsersWidget from './Widgets/TerminatedUsersWidget';
import JobHistoryWidget from './Widgets/JobHistoryWidget';

const NetworkDashboard = ({ 
  stats, 
  domains, 
  jobs, 
  terminatedUsers, 
  networkJobLogs, 
  changeView 
}) => {
  // Network job log columns for DataGrid
  const jobLogColumns = [
    { field: 'JOB_NAME', headerName: 'Job Name', flex: 1 },
    { 
      field: 'DOMAIN_NAME', 
      headerName: 'Domain', 
      flex: 1 
    },
    { 
      field: 'LAST_RUN_DATE', 
      headerName: 'Last Run', 
      width: 180,
      valueFormatter: (params) => params && params.value ? formatDate(params.value) : 'Never'
    },
    { 
      field: 'STATUS', 
      headerName: 'Status', 
      width: 120,
      renderCell: (params) => (
        <mui.Chip 
          label={params.row.STATUS} 
          color={params.row.STATUS === 'SUCCESS' ? 'success' : 'error'} 
          size="small" 
        />
      )
    },
    { 
      field: 'AFFECTED_USERS', 
      headerName: 'Affected Users', 
      width: 130,
      type: 'number'
    },
  ];

  // Calculate the stats you need to display
  const terminatedHighRisk = terminatedUsers.filter(u => u.RISK_LEVEL === 'HIGH').length;
  const jobSuccessRate = networkJobLogs.length > 0 
    ? Math.round((networkJobLogs.filter(log => log.STATUS === 'SUCCESS').length / networkJobLogs.length) * 100) 
    : 0;

  return (
    <mui.Box>
      <SectionHeader 
        title="Active Directory Management Dashboard" 
        icon={<SecurityIcon />} 
        description="Monitor network access control and governance" 
      />

      <StatsOverview stats={stats} />

      <mui.Grid container spacing={3} sx={{ mb: 4, mt: 2 }}>
        <mui.Grid item xs={12} md={6} lg={3}>
          <DashboardCard
            title="Domain Management"
            description="Configure domain servers and connections"
            icon={<StorageIcon fontSize="large" />}
            onClick={() => changeView('domains', 'Domain Management')}
            data={`${domains.length} Domains`}
            color="primary"
          />
        </mui.Grid>
        <mui.Grid item xs={12} md={6} lg={3}>
          <DashboardCard
            title="Job Scheduling"
            description="Manage automated jobs and schedules"
            icon={<ScheduleIcon fontSize="large" />}
            onClick={() => changeView('jobs', 'Job Scheduling')}
            data={`${jobs.length} Jobs`}
            color="secondary"
          />
        </mui.Grid>
        <mui.Grid item xs={12} md={6} lg={3}>
          <DashboardCard
            title="Terminated Users"
            description="Analysis of terminated users with active accounts"
            icon={<VerifiedUserIcon fontSize="large" />}
            onClick={() => changeView('analysis', 'Terminated User Analysis')}
            data={`${terminatedHighRisk} High Risk`}
            color="error"
          />
        </mui.Grid>
        <mui.Grid item xs={12} md={6} lg={3}>
          <DashboardCard
            title="Job History"
            description="Review job execution history and logs"
            icon={<HistoryIcon fontSize="large" />}
            onClick={() => changeView('logs', 'Job History')}
            data={`${jobSuccessRate}% Success Rate`}
            color="success"
          />
        </mui.Grid>
        <mui.Grid item xs={12} md={6} lg={3}>
          <DashboardCard
            title="Network Admins"
            description="View Domain Admins and privileged user accounts"
            icon={<AdminPanelSettingsIcon fontSize="large" />}
            onClick={() => changeView('network-admins', 'Network Admins')}
            data="Privileged Users"
            color="warning"
          />
        </mui.Grid>
        <mui.Grid item xs={12} md={6} lg={3}>
          <DashboardCard
            title="Reports"
            description="Generate reports for compliance and auditing"
            icon={<AssessmentIcon fontSize="large" />}
            onClick={() => changeView('reports', 'Reports')}
            data="Compliance Reporting"
            color="info"
          />
        </mui.Grid>
      </mui.Grid>

      <mui.Grid container spacing={4}>
        <mui.Grid item xs={12} md={6}>
          <TerminatedUsersWidget 
            terminatedUsers={terminatedUsers} 
            onClick={() => changeView('analysis', 'Terminated User Analysis')} 
          />
        </mui.Grid>
        <mui.Grid item xs={12} md={6}>
          <JobHistoryWidget 
            jobHistory={networkJobLogs} 
            onClick={() => changeView('logs', 'Job History')} 
          />
        </mui.Grid>
      </mui.Grid>

      <mui.Box sx={{ mb: 4 }}>
        <SectionHeader 
          title="Recent Job Executions" 
          icon={<HistoryIcon />} 
          description="Recent network job execution history" 
          action={
            <mui.Button 
              endIcon={<ChevronRightIcon />}
              onClick={() => changeView('logs', 'Job History')}
            >
              View All
            </mui.Button>
          }
        />
        
        {networkJobLogs.length > 0 ? (
          <mui.Paper sx={{ height: 300, width: '100%' }}>
            <DataGrid
              rows={networkJobLogs.slice(0, 5)}
              columns={jobLogColumns}
              pageSize={5}
              hideFooter
              disableSelectionOnClick
              getRowId={(row, index) => row.id || index}
            />
          </mui.Paper>
        ) : (
          <mui.Paper sx={{ p: 3, textAlign: 'center' }}>
            <HistoryIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <mui.Typography variant="body1" gutterBottom>
              No job execution history available
            </mui.Typography>
            <mui.Typography variant="body2" color="text.secondary">
              Job history will be displayed once jobs have been executed
            </mui.Typography>
          </mui.Paper>
        )}
      </mui.Box>
    </mui.Box>
  );
};

export default NetworkDashboard; 